<template>
  <div class="event-registration-modal">
    <b-modal
      :visible="visible"
      @update:visible="$emit('update:visible', $event)"
      @hide="handleHide"
      :title="modalTitle"
      hide-footer
      centered
      size="xl"
    >
      <b-tabs v-model="carpoolTabIndex" content-class="mt-3" @input="handleTabInput">
        <!-- Onglet Détails (événement admin) ou Inscription (ouverture) -->
        <b-tab>
          <template #title>
            <span v-if="isAdminEvent">
              <b-icon icon="info-circle" class="mr-1"></b-icon>
              Détails
            </span>
            <span v-else>
              <b-icon icon="pencil-square" class="mr-1"></b-icon>
              Inscription
            </span>
          </template>

          <div v-if="isAdminEvent" class="admin-event-details">
            <p class="mb-2">
              <strong>Date :</strong> {{ adminEventDateLabel }}
            </p>

            <div v-if="event.description" class="mb-2">
              <div
                class="admin-event-description rich-text-content text-muted mt-2"
                v-html="renderAdminEventDescription(event.description)"
              ></div>
            </div>

            <p class="mb-0">
              <strong>Prix :</strong> {{ adminEventPriceLabel }}
            </p>
          </div>

          <template v-else>
          <div class="planning-section mb-4">
            <div class="planning-section__header">
              <b-icon icon="clock" class="mr-2 text-primary"></b-icon>
              <span>Planning de l'ouverture</span>
            </div>
            <div v-if="openingEventsList.length" class="planning-slots">
              <div
                v-for="(openingEvent, index) in openingEventsList"
                :key="openingEventKey(openingEvent, index)"
                class="planning-slot"
                :class="openingEvent.type === 'ouverture' ? 'planning-slot--open' : 'planning-slot--close'"
              >
                <div class="planning-slot__content">
                  <div class="planning-slot__time">
                    <b-icon :icon="openingEvent.type === 'ouverture' ? 'box-arrow-right' : 'box-arrow-in-right'" class="planning-slot__icon-type"></b-icon>
                    <b-icon icon="clock" :rotate="index * 120" class="planning-slot__icon-clock"></b-icon>
                    {{ formatOpeningEventTime(openingEvent.time) }}
                  </div>
                  <div class="planning-slot__label">{{ formatOpeningEventType(openingEvent.type) }}</div>
                  <div class="planning-slot__person">{{ formatOpeningEventName(openingEvent.user) }}</div>
                </div>
              </div>
            </div>
            <p v-else class="text-muted mb-0 mt-2">Aucun horaire renseigné.</p>
          </div>

          <b-form @submit.prevent="handleRegistrationSubmit">
            <div class="gear-collapsible mb-4">
              <button
                type="button"
                class="gear-collapsible__toggle"
                :class="{ 'gear-collapsible__toggle--open': gearSectionOpen }"
                :aria-expanded="gearSectionOpen ? 'true' : 'false'"
                @click="gearSectionOpen = !gearSectionOpen"
              >
                <span class="gear-collapsible__toggle-left">
                  <b-icon icon="gear" class="mr-2"></b-icon>
                  Matériel <span class="text-muted font-weight-normal"> (optionnel)</span>
                </span>
                <span class="gear-collapsible__summary" v-if="!gearSectionOpen && gearSummary">
                  {{ gearSummary }}
                </span>
                <b-icon :icon="gearSectionOpen ? 'chevron-up' : 'chevron-down'" class="gear-collapsible__chevron"></b-icon>
              </button>

              <b-collapse v-model="gearSectionOpen">
                <div class="gear-collapsible__body">
                  <b-alert v-if="gearEditingDisabled" variant="warning" show class="gear-notice mb-3">
                    <b-icon icon="lock-fill" class="mr-2"></b-icon>
                    Matériel déjà retiré : la modification n'est plus possible.
                  </b-alert>
                  <b-alert v-else variant="info" show class="gear-notice mb-3">
                    <b-icon icon="info-circle" class="mr-2"></b-icon>
                    La sélection du matériel est un <strong>préremplissage uniquement</strong> — ce n'est pas une réservation.
                  </b-alert>

                  <div class="gear-checkboxes mb-3">
                    <div
                      class="gear-checkbox-item"
                      :class="{ 'gear-checkbox-item--checked': registrationForm.gear.harnais, 'gear-checkbox-item--disabled': gearEditingDisabled }"
                      @click="!gearEditingDisabled && (registrationForm.gear.harnais = !registrationForm.gear.harnais)"
                    >
                      <b-icon :icon="registrationForm.gear.harnais ? 'check-square-fill' : 'square'" class="mr-2"></b-icon>
                      Harnais
                    </div>
                    <div
                      class="gear-checkbox-item"
                      :class="{ 'gear-checkbox-item--checked': registrationForm.gear.combi, 'gear-checkbox-item--disabled': gearEditingDisabled }"
                      @click="!gearEditingDisabled && (registrationForm.gear.combi = !registrationForm.gear.combi)"
                    >
                      <b-icon :icon="registrationForm.gear.combi ? 'check-square-fill' : 'square'" class="mr-2"></b-icon>
                      Combinaison
                    </div>
                  </div>

                  <b-form-group label="Voile" class="mb-2">
                    <b-input-group size="sm">
                      <b-form-input
                        :value="registrationForm.gear.sail ? registrationForm.gear.sail.full_name : null"
                        readonly
                        placeholder="Pas de voile nécessaire"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button variant="outline-primary" size="sm" :disabled="gearEditingDisabled" @click="openGearModal('voile')">Rechercher</b-button>
                        <b-button v-if="registrationForm.gear.sail" variant="outline-danger" size="sm" :disabled="gearEditingDisabled" @click="clearGearSelection('sail')">Effacer</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>

                  <b-form-group label="Planche" class="mb-0">
                    <b-input-group size="sm">
                      <b-form-input
                        :value="registrationForm.gear.board ? registrationForm.gear.board.full_name : null"
                        readonly
                        placeholder="Pas de planche nécessaire"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button variant="outline-primary" size="sm" :disabled="gearEditingDisabled" @click="openGearModal('planche')">Rechercher</b-button>
                        <b-button v-if="registrationForm.gear.board" variant="outline-danger" size="sm" :disabled="gearEditingDisabled" @click="clearGearSelection('board')">Effacer</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </div>
              </b-collapse>
            </div>

            <div class="howto-section mb-4">
              <div class="howto-section__header">
                <b-icon icon="info-circle-fill" class="mr-2 text-primary"></b-icon>
                <span>Comment ça se passe le jour J ?</span>
              </div>
              <ol class="howto-list">
                <li>
                  <span>Arrive à l'heure d'ouverture indiquée dans le planning. Les créneaux d'ouverture et fermeture ne durent que <span class="howto-hint" tabindex="0" v-b-tooltip.hover.focus.top="{ customClass: 'howto-tooltip', title: tooltip30min }">30 minutes</span>, il faut être efficace.</span>
                </li>
                <li>
                  <span>Présente-toi à l'ouvreur en lui indiquant que tu t'es préinscrit sur le portail.</span>
                </li>
                <li>
                  <span>Récupère ton matériel puis indique à l'ouvreur <strong>AVANT d'aller naviguer</strong> le <span class="howto-hint" tabindex="0" v-b-tooltip.hover.focus.top="{ customClass: 'howto-tooltip', title: tooltipMateriel }">matériel</span> que tu vas utiliser même si tu avais préinscrit certains éléments.</span>
                </li>
                <li>
                  <span>Dépose ton matériel sur la plage en face du parking, soit délicat, le matériel est fragile et très cher. Regarde le <a href="#">guide du respect du matériel</a>.</span>
                </li>
                <li>
                  <span>Après ta nav, vérifie que le matériel que tu as utilisé n'est pas endommagé puis <u>confirme à l'ouvreur</u> que tu as bien tout rendu et que rien n'est abîmé.</span>
                </li>
                <li>
                  <span>Vérifie avant de partir que tu n'as rien oublié au local !</span>
                </li>
              </ol>
            </div>
          </b-form>
          </template>
        </b-tab>

        <!-- Onglet Covoiturage -->
        <b-tab>
          <template #title>
            <b-icon icon="car-front" class="mr-1"></b-icon>
            Covoiturage
          </template>
          <div class="carpool-layout">
            <div class="carpool-header mb-3">
              <h5 class="mb-1">Covoiturage</h5>
              <p class="text-muted mb-0">
                Choisis une action pour continuer.
              </p>
            </div>

            <div class="carpool-switch mb-3">
              <b-button-group>
                <b-button
                  :variant="carpoolMode === 'join' ? 'primary' : 'outline-primary'"
                  @click="setCarpoolMode('join')"
                >
                  <b-icon icon="people" class="mr-1"></b-icon>
                  Rejoindre un covoit
                </b-button>
                <b-button
                  :variant="carpoolMode === 'offer' ? 'primary' : 'outline-primary'"
                  :disabled="hasActiveCovoit"
                  @click="setCarpoolMode('offer')"
                >
                  <b-icon icon="plus-circle" class="mr-1"></b-icon>
                  Proposer un covoit
                </b-button>
              </b-button-group>
            </div>

            <div class="carpool-card">
              <div class="carpool-card__title">
                <b-icon
                  :icon="carpoolMode === 'offer' ? 'plus-circle' : 'people'"
                  class="mr-2"
                ></b-icon>
                {{ carpoolMode === 'offer' ? 'Proposer un covoit' : 'Rejoindre un covoit' }}
              </div>
              <div class="carpool-card__body">
                <div v-if="carpoolMode === 'offer'">
                  <div v-if="!isLogged" class="carpool-login-cta text-center py-4">
                    <b-icon icon="lock-fill" font-scale="2" class="text-muted d-block mx-auto mb-2"></b-icon>
                    <p class="text-muted mb-3">Connectez-vous pour proposer un covoit.</p>
                    <b-button variant="primary" @click="promptLogin">
                      <b-icon icon="box-arrow-in-right" class="mr-1"></b-icon>
                      Se connecter
                    </b-button>
                  </div>

                  <template v-else>
                  <div v-if="hasActiveCovoit" class="text-muted py-3">
                    Vous avez deja un covoit actif pour cette ouverture. Supprimez-le ou desinscrivez-vous avant d'en proposer un autre.
                  </div>

                  <!-- Profils sauvegardés -->
                  <div v-if="covoitProfiles.length > 0 || covoitProfilesLoading" class="mb-3 p-2 bg-light rounded">
                    <div class="d-flex align-items-center flex-wrap">
                      <small class="text-muted font-weight-bold mr-2 mb-1">Mes profils :</small>
                      <span v-if="covoitProfilesLoading" class="text-muted small mb-1">Chargement...</span>
                      <template v-else>
                        <div
                          v-for="profile in covoitProfiles"
                          :key="String(profile._id)"
                          class="d-inline-flex align-items-center mr-1 mb-1"
                        >
                          <b-button
                            size="sm"
                            :variant="selectedProfileId === String(profile._id) ? 'primary' : 'outline-secondary'"
                            class="profile-chip"
                            @click="applyProfile(profile)"
                          >
                            <b-icon icon="person-fill" class="mr-1"></b-icon>
                            {{ profile.name }}
                            <span class="text-muted small ml-1">({{ profile.seats }}pl)</span>
                          </b-button>
                          <b-button
                            size="sm"
                            variant="link"
                            class="p-0 ml-1 text-danger profile-delete-btn"
                            title="Supprimer ce profil"
                            @click="deleteProfile(profile)"
                          >
                            <b-icon icon="x"></b-icon>
                          </b-button>
                        </div>
                      </template>
                    </div>
                  </div>

                  <b-form @submit.prevent="submitCreateCovoit">
                    <b-form-group label="Point GPS de départ">
                      <MapLocationPicker
                        ref="mapLocationPicker"
                        v-model="carpoolForm.locationGPS"
                        :disabled="createCovoitLoading"
                        @resolved-address="handleResolvedAddress"
                      />
                    </b-form-group>

                    <b-form-group label="Adresse de départ">
                      <b-input-group>
                        <b-input-group-prepend is-text>
                          <b-icon icon="geo-alt"></b-icon>
                        </b-input-group-prepend>
                        <b-form-input
                          :value="carpoolForm.location"
                          readonly
                          placeholder="Sélectionnez un point sur la carte..."
                        ></b-form-input>
                      </b-input-group>
                      <small class="text-muted">Remplie automatiquement depuis le point GPS.</small>
                    </b-form-group>

                    <b-form-group label="Heure de départ">
                      <HourMinutePicker
                        v-model="carpoolForm.time"
                      />
                    </b-form-group>

                    <b-form-group label="Places proposées">
                      <b-input-group>
                        <b-input-group-prepend is-text>
                          <b-icon icon="people-fill"></b-icon>
                        </b-input-group-prepend>
                        <b-form-input
                          v-model.number="carpoolForm.seats"
                          type="number"
                          min="1"
                          max="15"
                          step="1"
                          placeholder="Ex: 3"
                        ></b-form-input>
                      </b-input-group>
                    </b-form-group>

                    <b-form-group label="Modèle de voiture">
                      <b-input-group>
                        <b-input-group-prepend is-text>
                          <b-icon icon="car-front"></b-icon>
                        </b-input-group-prepend>
                        <b-form-input
                          v-model.trim="carpoolForm.car_model"
                          placeholder="Ex: Renault Scenic noir"
                        ></b-form-input>
                      </b-input-group>
                    </b-form-group>

                    <b-form-group label="Infos">
                      <b-form-textarea
                        v-model.trim="carpoolForm.info"
                        placeholder="Ex: Point de rendez-vous précis, code portail, remarques..."
                        rows="2"
                        max-rows="4"
                      ></b-form-textarea>

                      <b-card no-body class="text-center mt-1">
                        <div class="bg-primary text-light p-1">
                          <b-icon icon="lightbulb-fill" mr-2></b-icon>
                          Protection de tes données : n'indiques pas ton numéro de téléphone ici, il est déjà affiché aux passagers dans le menu "mes covoiturages"
                        </div>
                      </b-card>
                    </b-form-group>

                    <b-button
                      :disabled="createCovoitLoading || !canCreateCovoit"
                      :variant="canCreateCovoit ? 'primary' : 'secondary'"
                      block
                      @click="submitCreateCovoit"
                    >
                      {{ createCovoitLoading ? "Publication..." : "Publier l'offre" }}
                    </b-button>

                    <!-- Sauvegarder comme profil -->
                    <div class="mt-3 border-top pt-2">
                      <div v-if="!saveProfileMode">
                        <b-button
                          variant="link"
                          size="sm"
                          class="p-0 text-muted"
                          @click="saveProfileMode = true; saveProfileError = ''"
                        >
                          <b-icon icon="bookmark" class="mr-1"></b-icon>
                          Créer un profil 
                        </b-button>
                      </div>
                      <div v-else>
                        <small class="text-muted d-block mb-1">Nom du profil :</small>
                        <b-input-group size="sm">
                          <b-form-input
                            v-model.trim="newProfileName"
                            placeholder="Ex: Maison, Travail..."
                            autofocus
                            @keyup.enter="saveAsProfile"
                          ></b-form-input>
                          <b-input-group-append>
                            <b-button
                              variant="success"
                              :disabled="!newProfileName.trim() || saveProfileLoading"
                              @click="saveAsProfile"
                            >
                              {{ saveProfileLoading ? "..." : "Sauvegarder" }}
                            </b-button>
                            <b-button
                              variant="outline-secondary"
                              @click="saveProfileMode = false; newProfileName = ''; saveProfileError = ''"
                            >
                              Annuler
                            </b-button>
                          </b-input-group-append>
                        </b-input-group>
                        <small v-if="saveProfileError" class="text-danger d-block mt-1">{{ saveProfileError }}</small>
                      </div>
                    </div>
                  </b-form>
                  </template>
                </div>

                <div v-else>
                  <div v-if="covoitsLoading" class="text-center text-muted py-4">
                    <b-spinner small class="mr-2"></b-spinner>Chargement des covoits...
                  </div>
                  <div v-else-if="covoitsError" class="text-danger py-2">
                    {{ covoitsError }}
                  </div>
                  <div v-else-if="!covoits.length" class="covoit-empty py-4 text-center text-muted">
                    <b-icon icon="car-front" font-scale="2" class="mb-2 d-block mx-auto"></b-icon>
                    Aucun covoiturage proposé pour cette ouverture.
                  </div>
                  <div v-else class="covoit-list">
                    <div
                      v-for="(covoit, index) in covoits"
                      :key="covoitKey(covoit, index)"
                      :class="['covoit-card', { 'covoit-card--full': isCovoitFull(covoit), 'covoit-card--mine': isDriver(covoit), 'covoit-card--joined': isPassenger(covoit) }]"
                    >
                      <!-- Miniature carte GPS ou avatar fallback -->
                      <div
                        class="covoit-card__avatar"
                        :class="{ 'covoit-card__avatar--clickable': hasLocationGPS(covoit) }"
                        @click="hasLocationGPS(covoit) && openCovoitLocation(covoit)"
                      >
                        <CovoitMapThumb
                          v-if="hasLocationGPS(covoit)"
                          :lat="covoit.locationGPS.latitude"
                          :lng="covoit.locationGPS.longitude"
                        />
                        <div v-else class="driver-avatar">
                          {{ driverInitials(covoit.driver) }}
                        </div>
                      </div>

                      <!-- Infos principales -->
                      <div class="covoit-card__body">
                        <div class="covoit-card__title">
                          {{ formatUserName(covoit.driver) }}
                          <span v-if="isDriver(covoit)" class="covoit-badge covoit-badge--mine ml-1">Votre offre</span>
                          <span v-else-if="isPassenger(covoit)" class="covoit-badge covoit-badge--joined ml-1">Inscrit</span>
                        </div>

                        <div class="covoit-card__meta">
                          <span class="covoit-meta-item">
                            <b-icon icon="geo-alt-fill" class="mr-1 text-primary"></b-icon>{{ covoit.location }}
                          </span>
                          <span class="covoit-meta-item">
                            <b-icon icon="clock" class="mr-1"></b-icon>{{ formatCovoitTime(covoit.time) }}
                          </span>
                          <span v-if="covoit.car_model" class="covoit-meta-item text-muted">
                            <b-icon icon="car-front" class="mr-1"></b-icon>{{ covoit.car_model }}
                          </span>
                        </div>

                        <div class="covoit-card__stats-mobile mb-1">
                          <span
                            class="covoit-stat-mini"
                            :class="isCovoitFull(covoit) ? 'covoit-stat-mini--full' : 'covoit-stat-mini--available'"
                          >
                            <b-icon icon="people-fill" class="mr-1"></b-icon>{{ availableSeats(covoit) }} place{{ availableSeats(covoit) !== 1 ? 's' : '' }}
                          </span>
                          <span class="covoit-stat-mini covoit-stat-mini--neutral">
                            <b-icon icon="person-fill" class="mr-1"></b-icon>{{ covoitPassengerCount(covoit) }}  passager{{ covoitPassengerCount(covoit) !== 1 ? 's' : '' }}
                          </span>
                        </div>

                        <div v-if="covoit.info" class="covoit-card__info">
                          <b-icon icon="chat-left-text" class="mr-1"></b-icon>{{ covoit.info }}
                        </div>

                        <div class="covoit-card__actions">
                          <b-button
                            v-if="isDriver(covoit)"
                            size="sm"
                            variant="outline-primary"
                            class="covoit-action-btn"
                            @click="goToEditCovoit"
                          >
                            <b-icon icon="pencil" class="mr-1"></b-icon>Modifier
                          </b-button>
                          <b-button
                            v-else-if="isPassenger(covoit)"
                            size="sm"
                            variant="outline-danger"
                            :disabled="covoitActionLoadingId === getCovoitId(covoit)"
                            class="covoit-action-btn"
                            @click="leaveCovoit(covoit)"
                          >
                            <b-spinner v-if="covoitActionLoadingId === getCovoitId(covoit)" small class="mr-1"></b-spinner>
                            <b-icon v-else icon="x-circle" class="mr-1"></b-icon>Se désinscrire

                          </b-button>

                          <b-button
                            v-else
                            size="sm"
                            :variant="isCovoitFull(covoit) ? 'outline-secondary' : 'primary'"
                            :disabled="isCovoitFull(covoit) || !isLogged || covoitActionLoadingId === getCovoitId(covoit) || hasActiveCovoit"
                            class="covoit-action-btn"
                            @click="joinCovoit(covoit)"
                          >
                            <b-spinner v-if="covoitActionLoadingId === getCovoitId(covoit)" small class="mr-1"></b-spinner>
                            <b-icon v-else icon="person-plus" class="mr-1"></b-icon>
                            {{ isCovoitFull(covoit) ? 'Complet' : 'Réserver une place' }}
                          </b-button>
                          <b-button
                            v-if="hasLocationGPS(covoit)"
                            size="sm"
                            variant="outline-primary"
                            :disabled="covoitActionLoadingId === getCovoitId(covoit)"
                            class="covoit-action-btn"
                            @click="openCovoitLocation(covoit)"
                          >
                            <b-icon icon="map" class="mr-1"></b-icon>Carte
                          </b-button>
                        </div>
                      </div>

                      <!-- Stats à droite -->
                      <div class="covoit-card__stats">
                        <div class="covoit-stat" :class="isCovoitFull(covoit) ? 'covoit-stat--full' : 'covoit-stat--available'">
                          <b-icon icon="people-fill" class="covoit-stat__icon"></b-icon>
                          <span class="covoit-stat__value">{{ availableSeats(covoit) }}</span>
                          <span class="covoit-stat__label">place{{ availableSeats(covoit) !== 1 ? 's' : '' }}</span>
                        </div>
                        <div class="covoit-stat covoit-stat--neutral">
                          <b-icon icon="person-fill" class="covoit-stat__icon"></b-icon>
                          <span class="covoit-stat__value">{{ covoitPassengerCount(covoit) }}</span>
                          <span class="covoit-stat__label">passager{{ covoitPassengerCount(covoit) !== 1 ? 's' : '' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
      <div v-if="requiresAdhesionWarning" class="text-danger text-right mt-4 mb-n2 font-weight-bold">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        Une adhésion valide est requise pour s'inscrire à une ouverture.
      </div>
      <div class="mt-4 d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <b-button
            v-if="mode === 'manage'"
            variant="danger"
            :disabled="unregisterLoading || gearEditingDisabled"
            :title="gearEditingDisabled ? 'Matériel déjà retiré, désinscription impossible.' : ''"
            @click="confirmUnregister"
          >
            <b-spinner v-if="unregisterLoading" small class="mr-1"></b-spinner>
            <b-icon v-else icon="x-circle" class="mr-1"></b-icon>
            Se désinscrire
          </b-button>
        </div>
        <div>
          <b-button variant="secondary" @click="close" class="mr-2">
            {{ (mode === 'manage' || isAdminEvent) ? 'Fermer' : 'Annuler' }}
          </b-button>
          <b-button
            v-if="!isAdminEvent && !(mode === 'manage' && carpoolTabIndex === 1)"
            variant="primary"
            :disabled="registrationSubmitLoading || gearEditingDisabled || requiresAdhesionWarning"
            @click="handleRegistrationSubmit"
          >
            <b-spinner v-if="registrationSubmitLoading" small class="mr-1"></b-spinner>
            {{ mode === 'manage' ? 'Enregistrer les modifications' : "Se préinscrire à l'ouverture" }}
          </b-button>
        </div>
      </div>
      <GearSelectModal
        :visible.sync="gearModalVisible"
        :type="gearModalType"
        @selected="handleGearSelected"
      />
  </b-modal>

  </div>
</template>

<script>
import GearSelectModal from "@/components/GearSelectModal.vue";
import HourMinutePicker from "@/components/HourMinutePicker.vue";
import MapLocationPicker from "@/components/MapLocationPicker.vue";
import CovoitMapThumb from "@/components/CovoitMapThumb.vue";
import OpeningService from "@/services/opening.service";
import UserService from "@/services/user.service";
import session from "@/store/session";
import { sanitizeRichText } from "@/utils/richText";

export default {
  name: 'EventRegistrationModal',
  components: {
    GearSelectModal,
    HourMinutePicker,
    MapLocationPicker,
    CovoitMapThumb
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    closingEvents: {
      type: Array,
      default: () => []
    },
    mode: {
      type: String,
      default: "register",
      validator: (value) => ["register", "manage"].includes(value)
    },
    initialTab: {
      type: Number,
      default: 0
    },
    userHasValidAdhesion: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tooltip30min: "Les ouvreurs sont bénévoles et naviguent eux aussi. Une fois le matériel choisi, assure toi de tout avoir et déplace tout dehors pour gréer.",
      tooltipMateriel: "Donne lui le numéro de ta voile commençant par GF, le modèle de ta planche et son numéro (ex: Beach 175 n° 3) ainsi que si tu emprunte une combinaison et un harnais.",
      gearModalVisible: false,
      gearModalType: "voile",
      carpoolTabIndex: 0,
      carpoolMode: "join",
      covoits: [],
      covoitsLoading: false,
      covoitsError: "",
      covoitActionLoadingId: null,
      createCovoitLoading: false,
      carpoolForm: {
        location: "",
        locationGPS: null,
        time: "",
        seats: 1,
        car_model: "",
        info: ""
      },
      covoitProfiles: [],
      covoitProfilesLoading: false,
      selectedProfileId: null,
      saveProfileMode: false,
      newProfileName: "",
      saveProfileLoading: false,
      saveProfileError: "",
      gearSectionOpen: false,
      registrationSubmitLoading: false,
      unregisterLoading: false,
      registrationForm: {
        gear: {
          harnais: false,
          combi: false,
          sail: null,
          board: null
        },
      }
    }
  },
  created() {
    this.loadCovoits();
    this.loadCovoitProfiles();
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.carpoolTabIndex = this.initialTab;
        if (this.mode === "manage") {
          this.prefillFromParticipant();
        }
        this.loadCovoits();
        this.loadCovoitProfiles();
        if (this.carpoolTabIndex === 1) {
          this.refreshMapDisplay();
        }
      }
    }
  },
  computed: {
    modalTitle() {
      if (this.isAdminEvent) return this.openingTitle;
      return `Permanence du ${this.adminEventDateLabel}`;
    },
    isAdminEvent() {
      return this.event.openingType === "ADMIN_EVENT";
    },
    openingTitle() {
      return this.event.title;
    },
    
    adminEventPriceLabel() {
      return this.event.price;
    },
    adminEventDateLabel() {
      const date = this.getPrimaryEventDate();
      if (Number.isNaN(date.getTime())) return "Date à confirmer";
      return date.toLocaleString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    openingEventsList() {
      if (!Array.isArray(this.event.openingEvents)) return [];
      return [...this.event.openingEvents].sort((a, b) => {
        const aTime = this.parseTimestamp(a.time);
        const bTime = this.parseTimestamp(b.time);
        return aTime - bTime;
      });
    },
    covoitsList() {
      return this.covoits;
    },
    openingId() {
      return this.getOpeningId();
    },
    currentUserId() {
      const user = session.user;
      if (!user) return null;
      return String(user.id);
    },
    isLogged() {
      return session.isLoggedIn();
    },
    gearEditingDisabled() {
      return this.mode === "manage" && this.event && this.event.participantInfos && this.event.participantInfos.checkedOut === true;
    },
    requiresAdhesionWarning() {
      return !this.isAdminEvent && this.mode !== "manage" && !this.userHasValidAdhesion;
    },
    canCreateCovoit() {
      return (
        this.isLogged &&
        !this.hasActiveCovoit &&
        !!this.carpoolForm.time &&
        Number(this.carpoolForm.seats) > 0 &&
        this.hasValidLocationGPS(this.carpoolForm.locationGPS)
      );
    },
    hasActiveCovoit() {
      return this.covoitsList.some((covoit) => this.isDriver(covoit) || this.isPassenger(covoit));
    },
    gearSummary() {
      const parts = [];
      const g = this.registrationForm.gear;
      if (g.harnais) parts.push("harnais");
      if (g.combi) parts.push("combi");
      if (g.sail) parts.push(g.sail.full_name || "voile");
      if (g.board) parts.push(g.board.full_name || "planche");
      return parts.join(", ");
    }
  },
  methods: {
    parseTimestamp(value) {
      const parsed = new Date(value).getTime();
      if (Number.isNaN(parsed)) return 0;
      return parsed;
    },
    openingEventKey(openingEvent, index) {
      if (openingEvent._id) return openingEvent._id;
      return `opening-event-${index}`;
    },
    covoitKey(covoit, index) {
      const id = this.getCovoitId(covoit);
      if (id) return id;
      return `covoit-${index}`;
    },
    getPrimaryEventDate() {
      return new Date(this.event.openingDate);
    },
    getOpeningId() {
      return this.event.id;
    },
    getCovoitId(covoit) {
      if (!covoit) return null;
      if (covoit._id) return String(covoit._id);
      return null;
    },
    getUserId(user) {
      return String(user._id);
    },
    hasLocationGPS(covoit) {
      if (!covoit || !covoit.locationGPS || typeof covoit.locationGPS !== "object") return false;
      return Number.isFinite(covoit.locationGPS.latitude) && Number.isFinite(covoit.locationGPS.longitude);
    },
    getCovoitCoordinates(covoit) {
      if (!this.hasLocationGPS(covoit)) return null;
      return {
        latitude: Number(covoit.locationGPS.latitude),
        longitude: Number(covoit.locationGPS.longitude)
      };
    },
    buildGoogleMapsUrl(coords, label) {
      const query = `${coords.latitude},${coords.longitude}`;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${query}`.trim())}`;
    },
    openUrl(url) {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    openCovoitLocation(covoit) {
      const coords = this.getCovoitCoordinates(covoit);
      if (!coords) return;

      const label = covoit && covoit.location ? covoit.location : "Point GPS";
      this.openUrl(this.buildGoogleMapsUrl(coords, label));
    },
    formatUserName(user) {
      if (!user) return "Conducteur";
      const name = [user.name, user.surname].filter(Boolean).join(" ").trim();
      if (name) return name;
      if (user.email) return user.email;
      return "Conducteur";
    },
    driverInitials(user) {
      if (!user) return "?";
      const n = (user.name || "").charAt(0).toUpperCase();
      const s = (user.surname || "").charAt(0).toUpperCase();
      return (n + s) || "?";
    },
    covoitPassengerCount(covoit) {
      return Array.isArray(covoit.passenger) ? covoit.passenger.length : 0;
    },
    availableSeats(covoit) {
      const seats = Number(covoit.seats);
      const passengers = Array.isArray(covoit.passenger) ? covoit.passenger.length : 0;
      return Math.max(seats - passengers, 0);
    },
    availableSeatsLabel(covoit) {
      const available = this.availableSeats(covoit);
      return available > 0 ? `${available} place${available > 1 ? "s" : ""}` : "Complet";
    },
    isCovoitFull(covoit) {
      return this.availableSeats(covoit) <= 0;
    },
    isDriver(covoit) {
      if (!this.currentUserId) return false;
      if (!covoit || !covoit.driver) return false;
      return this.getUserId(covoit.driver) === this.currentUserId;
    },
    isPassenger(covoit) {
      if (!this.currentUserId) return false;
      if (!covoit || !Array.isArray(covoit.passenger)) return false;
      return covoit.passenger.some(user => this.getUserId(user) === this.currentUserId);
    },
    buildCovoitDateTime(timeValue) {
      if (!timeValue) return null;
      const openingDate = String(this.event.openingDate).split("T")[0];
      const dateTime = new Date(`${openingDate}T${timeValue}`);
      if (Number.isNaN(dateTime.getTime())) return null;
      return dateTime.toISOString();
    },
    handleTabInput() {
      if (this.carpoolTabIndex === 1) {
        this.loadCovoits();
        this.loadCovoitProfiles();
        this.refreshMapDisplay();
      }
    },
    promptLogin() {
      // Ferme cette modale puis déclenche l'ouverture de la modale de connexion
      // gérée par AppHeader via la query ?login=required.
      this.$emit('update:visible', false);
      this.$router.push({ query: { ...this.$route.query, login: "required" } }).catch(() => {});
    },
    setCarpoolMode(mode) {
      if (mode === "offer" && this.hasActiveCovoit) return;
      this.carpoolMode = mode;
      if (mode === "join" && this.carpoolTabIndex === 1) {
        this.loadCovoits();
      }
      if (mode === "offer" && this.carpoolTabIndex === 1) {
        this.loadCovoitProfiles();
        this.refreshMapDisplay();
      }
    },
    refreshMapDisplay() {
      this.$nextTick(() => {
        if (this.$refs.mapLocationPicker && typeof this.$refs.mapLocationPicker.invalidateSize === "function") {
          this.$refs.mapLocationPicker.invalidateSize();
        }
      });
    },
    hasValidLocationGPS(value) {
      if (!value || typeof value !== "object") return false;
      return Number.isFinite(value.latitude) && Number.isFinite(value.longitude);
    },
    handleResolvedAddress(address) {
      if (address) {
        this.carpoolForm.location = address;
      } else if (this.hasValidLocationGPS(this.carpoolForm.locationGPS)) {
        const { latitude, longitude } = this.carpoolForm.locationGPS;
        this.carpoolForm.location = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      }
    },
    formatOpeningEventType(type) {
      if (type === "ouverture") return "Ouverture";
      if (type === "fermeture") return "Fermeture";
      return "Événement";
    },
    formatOpeningEventTime(time) {
      if (!time) return "heure inconnue";
      const date = new Date(time);
      if (Number.isNaN(date.getTime())) return "Date à confirmer";
      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Paris",
      });
    },
    renderAdminEventDescription(description) {
      return sanitizeRichText(description);
    },
    formatOpeningEventName(user) {
      return user.name + " " + user.surname;
    },
    formatCovoitTime(time) {
      if (!time) return "--:--";
      const date = new Date(time);
      if (Number.isNaN(date.getTime())) return "--:--";
      return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    },
    async loadCovoitProfiles() {
      if (!this.isLogged) return;
      this.covoitProfilesLoading = true;
      try {
        const res = await UserService.getCovoitProfiles();
        this.covoitProfiles = res.data.profiles || [];
      } catch (e) {
        this.covoitProfiles = [];
        console.error("Erreur chargement profils covoit :", e);
      } finally {
        this.covoitProfilesLoading = false;
      }
    },
    applyProfile(profile) {
      this.selectedProfileId = String(profile._id);
      this.carpoolForm.location = profile.location || "";
      this.carpoolForm.seats = profile.seats || 1;
      this.carpoolForm.car_model = profile.car_model || "";
      if (profile.locationGPS && Number.isFinite(profile.locationGPS.latitude) && Number.isFinite(profile.locationGPS.longitude)) {
        this.carpoolForm.locationGPS = { latitude: profile.locationGPS.latitude, longitude: profile.locationGPS.longitude };
        this.refreshMapDisplay();
      }
    },
    async saveAsProfile() {
      if (!this.newProfileName.trim()) return;
      this.saveProfileLoading = true;
      this.saveProfileError = "";
      try {
        const payload = {
          name: this.newProfileName.trim(),
          location: this.carpoolForm.location,
          locationGPS: this.carpoolForm.locationGPS,
          seats: Number(this.carpoolForm.seats),
          car_model: this.carpoolForm.car_model || ""
        };
        const res = await UserService.createCovoitProfile(payload);
        this.covoitProfiles.push(res.data.profile);
        this.saveProfileMode = false;
        this.newProfileName = "";
      } catch (e) {
        this.saveProfileError = e.message || "Impossible de sauvegarder le profil.";
      } finally {
        this.saveProfileLoading = false;
      }
    },
    async deleteProfile(profile) {
      try {
        await UserService.deleteCovoitProfile(String(profile._id));
        const idx = this.covoitProfiles.findIndex(p => String(p._id) === String(profile._id));
        if (idx !== -1) this.covoitProfiles.splice(idx, 1);
        if (this.selectedProfileId === String(profile._id)) this.selectedProfileId = null;
      } catch (e) {
        // silencieux
      }
    },
    async loadCovoits() {
      const ouvertureId = this.openingId;
      this.covoitsLoading = true;
      this.covoitsError = "";
      try {
        const response = await OpeningService.listCovoits(ouvertureId);
        this.covoits = response.data.covoits;
      } catch (error) {
        this.covoitsError = error.message ? error.message : "Impossible de récupérer les covoiturages.";
      } finally {
        this.covoitsLoading = false;
      }
    },
    async submitCreateCovoit() {
      if (!this.canCreateCovoit) return;
      const ouvertureId = this.openingId;

      let locationValue = this.carpoolForm.location;
      if (!locationValue && this.hasValidLocationGPS(this.carpoolForm.locationGPS)) {
        const { latitude, longitude } = this.carpoolForm.locationGPS;
        locationValue = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      }

      const payload = {
        location: locationValue,
        locationGPS: this.carpoolForm.locationGPS,
        seats: Number(this.carpoolForm.seats),
        time: this.buildCovoitDateTime(this.carpoolForm.time),
        car_model: this.carpoolForm.car_model ? this.carpoolForm.car_model : "",
        info: this.carpoolForm.info ? this.carpoolForm.info : ""
      };
      if (!payload.time) {
        console.log(payload);
        this.$bvToast && this.$bvToast.toast("Horaire de départ invalide.", {
          title: "Erreur",
          variant: "danger",
          solid: true
        });
        return;
      }
      this.createCovoitLoading = true;
      try {
        await OpeningService.createCovoit(ouvertureId, payload);
        this.carpoolForm = { location: "", locationGPS: null, time: "", seats: 1, car_model: "", info: "" };
        this.carpoolMode = "join";
        await this.loadCovoits();
      } catch (error) {
        if (!this.$bvToast) return;
        this.$bvToast.toast(error.message ? error.message : "Impossible de créer le covoiturage.", {
          title: "Erreur",
          variant: "danger",
          solid: true
        });
      } finally {
        this.createCovoitLoading = false;
      }
    },
    async joinCovoit(covoit) {
      if (!covoit || !this.isLogged) return;
      const ouvertureId = this.openingId;
      const covoitId = this.getCovoitId(covoit);
      this.covoitActionLoadingId = covoitId;
      try {
        await OpeningService.joinCovoit(ouvertureId, covoitId);
        await this.loadCovoits();
      } catch (error) {
        if (!this.$bvToast) return;
        this.$bvToast.toast(error.message ? error.message : "Impossible de rejoindre le covoiturage.", {
          title: "Erreur",
          variant: "danger",
          solid: true
        });
      } finally {
        this.covoitActionLoadingId = null;
      }
    },
    goToEditCovoit() {
      const ouvertureId = this.openingId;
      if (!ouvertureId) return;
      this.$emit('update:visible', false);
      this.$router.push({
        name: 'my-carpools',
        query: { ouvertureId: String(ouvertureId) }
      }).catch(() => {});
    },
    async leaveCovoit(covoit) {
      if (!covoit || !this.isLogged) return;
      const ouvertureId = this.openingId;
      const covoitId = this.getCovoitId(covoit);
      this.covoitActionLoadingId = covoitId;
      try {
        await OpeningService.leaveCovoit(ouvertureId, covoitId);
        await this.loadCovoits();
      } catch (error) {
        if (!this.$bvToast) return;
        this.$bvToast.toast(error.message ? error.message : "Impossible de quitter le covoiturage.", {
          title: "Erreur",
          variant: "danger",
          solid: true
        });
      } finally {
        this.covoitActionLoadingId = null;
      }
    },
    openGearModal(type) {
      this.gearModalType = type === "planche" ? "planche" : "voile";
      this.gearModalVisible = true;
    },
    handleGearSelected(value) {
      const field = this.gearModalType === "planche" ? "board" : "sail";
      this.$set(this.registrationForm.gear, field, value ? value : null);
      this.gearModalVisible = false;
    },
    clearGearSelection(field) {
      this.$set(this.registrationForm.gear, field, null);
    },
    async confirmUnregister() {
      const carpoolNote = this.hasActiveCovoit
        ? " Tu as un covoiturage lié à cette ouverture, pense à t'en désinscrire manuellement depuis l'onglet covoiturage."
        : "";
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous annuler votre inscription à "${this.event.title}" ?${carpoolNote}`,
        {
          title: "Se désinscrire",
          okVariant: "danger",
          okTitle: "Confirmer la désinscription",
          cancelTitle: "Annuler",
          centered: true
        }
      );
      if (!confirmed) return;
      this.unregisterLoading = true;
      try {
        const res = await OpeningService.cancelRegistration(this.openingId);
        if (res.status !== 200) {
          throw new Error("Impossible de se désinscrire.");
        }
        if (this.$bvToast) {
          this.$bvToast.toast("Inscription annulée.", {
            title: "Succès",
            variant: "success",
            solid: true
          });
        }
        this.$emit("updated");
        this.close();
      } catch (err) {
        const message = err && err.message ? err.message : "Erreur lors de la désinscription.";
        if (this.$bvToast) {
          this.$bvToast.toast(message, { title: "Erreur", variant: "danger", solid: true });
        }
      } finally {
        this.unregisterLoading = false;
      }
    },
    async handleRegistrationSubmit() {
      this.registrationSubmitLoading = true;
      try {
        if (this.mode === "manage") {
          const payload = {
            harnais: this.registrationForm.gear.harnais,
            combi: this.registrationForm.gear.combi,
            sail: this.registrationForm.gear.sail ? { _id: this.registrationForm.gear.sail._id } : null,
            board: this.registrationForm.gear.board ? { _id: this.registrationForm.gear.board._id } : null,
          };
          await OpeningService.updateMyRegistration(this.openingId, payload);
          if (this.$bvToast) {
            this.$bvToast.toast("Inscription mise a jour.", {
              title: "Succes",
              variant: "success",
              solid: true
            });
          }
          this.$emit("updated");
          this.close();
          return;
        }
        const payload = {
          harnais: this.registrationForm.gear.harnais,
          combi: this.registrationForm.gear.combi,
          sailId: this.registrationForm.gear.sail ? this.registrationForm.gear.sail._id : null,
          boardId: this.registrationForm.gear.board ? this.registrationForm.gear.board._id : null,
        };
        await OpeningService.registerParticipant(this.openingId, payload);
        this.close();
        window.location.reload();
      } catch (error) {
        console.error("Erreur lors de la soumission de l'inscription :", error);
        const message = error && error.message
          ? error.message
          : "Une erreur est survenue. Veuillez reessayer.";
        if (this.$bvToast) {
          this.$bvToast.toast(message, { title: "Erreur", variant: "danger", solid: true });
        } else {
          alert(message);
        }
      } finally {
        this.registrationSubmitLoading = false;
      }
    },
    prefillFromParticipant() {
      const participant = this.event && this.event.participantInfos
        ? this.event.participantInfos
        : null;
      if (!participant) {
        this.resetForm();
        return;
      }
      this.registrationForm.gear.harnais = participant.harnais === true;
      this.registrationForm.gear.combi = participant.combi === true;
      this.registrationForm.gear.sail = participant.sail && participant.sail._id ? participant.sail : null;
      this.registrationForm.gear.board = participant.board && participant.board._id ? participant.board : null;
      this.gearSectionOpen = !!(
        this.registrationForm.gear.harnais ||
        this.registrationForm.gear.combi ||
        this.registrationForm.gear.sail ||
        this.registrationForm.gear.board
      );
    },
    resetForm() {
      this.gearSectionOpen = false;
      this.registrationForm = {
        gear: {
          harnais: false,
          combi: false,
          sail: null,
          board: null
        },
      };
    },
    resetCarpoolState() {
      this.carpoolMode = "join";
      this.carpoolForm = { location: "", locationGPS: null, time: "", seats: 1, car_model: "" };
      this.covoits = [];
      this.covoitsError = "";
      this.covoitsLoading = false;
      this.covoitActionLoadingId = null;
      this.createCovoitLoading = false;
      this.selectedProfileId = null;
      this.saveProfileMode = false;
      this.newProfileName = "";
      this.saveProfileError = "";
    },
    close() {
      this.resetForm();
      this.resetCarpoolState();
      this.$emit('update:visible', false);
    },
    async handleHide(bvModalEvt) {
      this.carpoolTabIndex = 0;
      if (this.hasActiveCovoit && !this.event.isRegistered && !this.isAdminEvent) {
        bvModalEvt.preventDefault();
        const confirmed = await this.$bvModal.msgBoxConfirm(
          "Vous avez un covoiturage lié à cette ouverture mais vous n'êtes pas inscrit à la permanence. Voulez-vous quand même fermer ?",
          {
            title: "Covoiturage sans inscription",
            okVariant: "danger",
            okTitle: "Fermer quand même",
            cancelTitle: "Annuler",
            centered: true
          }
        );
        if (confirmed) {
          this.close();
        }
        return;
      }
      this.close();
    },

  }
}
</script>

<style scoped>
.profile-chip {
  font-size: 0.8rem;
}

.profile-delete-btn {
  font-size: 0.75rem;
  line-height: 1;
  opacity: 0.6;
}

.profile-delete-btn:hover {
  opacity: 1;
}

.admin-event-description {
  line-height: 1.5;
}

.rich-text-content >>> p,
.rich-text-content >>> ul,
.rich-text-content >>> ol {
  margin-bottom: 0.75rem;
}

.rich-text-content >>> p:last-child,
.rich-text-content >>> ul:last-child,
.rich-text-content >>> ol:last-child {
  margin-bottom: 0;
}

.rich-text-content >>> ul,
.rich-text-content >>> ol {
  padding-left: 1.25rem;
}

.rich-text-content >>> a {
  color: #007bff;
  text-decoration: underline;
}

/* ── Planning de l'ouverture ─────────────────────────────── */
.planning-section__header {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2a44;
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.planning-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.planning-slot {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: 0.6rem;
  padding: 0.6rem 0.9rem;
  border: 1px solid transparent;
  min-width: 148px;
  flex: 1 1 148px;
}

.planning-slot--open {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

.planning-slot--close {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.planning-slot__icon-clock,
.planning-slot__icon-type {
  font-size: 1rem;
  opacity: 0.75;
  vertical-align: middle;
  margin-right: 0.25rem;
}

.planning-slot__time {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.1;
  display: flex;
  align-items: center;
}

.planning-slot__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
  line-height: 1.2;
}

.planning-slot__person {
  font-size: 0.78rem;
  opacity: 0.8;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Comment ça se passe le jour J ───────────────────────── */
.howto-section {
  background: #f8fbff;
  border: 1px solid #d6e4f5;
  border-radius: 0.6rem;
  padding: 0.9rem 1rem;
}

.howto-section__header {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2a44;
  display: flex;
  align-items: center;
  margin-bottom: 0.6rem;
}

.howto-list {
  list-style: decimal;
  padding: 0;
  padding-left: 2.25rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.howto-list li {
  font-size: 0.88rem;
  line-height: 1.35;
  color: #2c3a59;
  padding-left: 0.35rem;
}

.howto-list li::marker {
  color: #3b82f6;
  font-weight: 700;
}

.howto-hint {
  text-decoration: underline dashed;
  text-decoration-color: #3b82f6;
  text-underline-offset: 0.2em;
  text-decoration-thickness: 1px;
  cursor: help;
}

/* ── Matériel collapsible ────────────────────────────────── */
.gear-collapsible {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
}

.gear-collapsible__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border: 0;
  cursor: pointer;
  text-align: left;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2a44;
  transition: background 0.15s;
}

.gear-collapsible__toggle:hover {
  background: #eef0f3;
}

.gear-collapsible__toggle--open {
  background: #eef0f3;
  border-bottom: 1px solid #dee2e6;
}

.gear-collapsible__toggle-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.gear-collapsible__summary {
  font-size: 0.78rem;
  font-weight: 400;
  color: #6c757d;
  margin-right: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.gear-collapsible__chevron {
  flex-shrink: 0;
  color: #6c757d;
}

.gear-collapsible__body {
  padding: 1rem;
  background: #fff;
}

.gear-notice {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0;
}

.gear-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.gear-checkbox-item {
  display: flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 400;
  font-size: 0.875rem;
  color: #495057;
  background: #f8f9fa;
  margin: 0;
  transition: border-color 0.15s, background 0.15s;
}

.gear-checkbox-item--checked {
  background: #e8f0fe;
  border-color: #9dd3dd;
  color: #056d80;
}

.gear-checkbox-item--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.carpool-layout {
  border-radius: 0.75rem;
  padding: 0;
}

.carpool-card {
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e4e8ef;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  height: 100%;
}

.carpool-card__title {
  font-weight: 600;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef1f5;
  display: flex;
  align-items: center;
  color: #1f2a44;
}

.carpool-card__body {
  padding: 1.25rem;
}

.carpool-switch .btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.carpool-switch .btn {
  border-radius: 999px;
}
.carpool-switch .btn-group > .btn:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.carpool-switch .btn-group > .btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* Covoit list */
.covoit-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.covoit-empty {
  color: #8a9ab0;
}

.covoit-card {
  display: flex;
  align-items: stretch;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e4e8ef;
  border-radius: 0.6rem;
  padding: 0.75rem 1rem;
  transition: border-color 0.15s;
}

.covoit-card:hover {
  border-color: #b8c5d6;
}

.covoit-card--full {
  background: #f8f9fb;
  border-color: #e4e8ef;
}

.covoit-card--mine {
  border-left: 3px solid var(--color-guc-darkblue);
}

.covoit-card--joined {
  border-left: 3px solid var(--color-guc-yellow);
}

/* Avatar */
.covoit-card__avatar {
  flex-shrink: 0;
  width: 88px;
  align-self: stretch;
  display: flex;
  flex-direction: column;
}

.covoit-card__avatar--clickable {
  cursor: pointer;
  position: relative;
}

.covoit-card__avatar--clickable::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0);
  transition: background 0.15s;
  pointer-events: none;
}

.covoit-card__avatar--clickable:hover::after {
  background: rgba(0, 0, 0, 0.12);
}

.driver-avatar {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  background-color: var(--color-guc-darkblue, #1a3a5c);
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
}

/* Body */
.covoit-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}


.covoit-card__title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2a44;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.2rem;
}

.covoit-badge {
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  display: inline-block;
}

.covoit-badge--mine {
  background: var(--color-guc-lightblue);
  color: var(--color-guc-darkdarkblue);
}

.covoit-badge--joined {
  background: #ffecd9;
  color: #b85a1a;
}

.covoit-card__meta {
  font-size: 0.82rem;
  color: #4a5568;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin-bottom: 0.2rem;
}

.covoit-card__info {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.2rem;
  font-style: italic;
}

.covoit-card__actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: auto;
}

.covoit-action-btn {
  border-radius: 999px !important;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
}

/* Stats */
.covoit-card__stats {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: center;
  min-width: 56px;
}

.covoit-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f1f5f9;
  border-radius: 0.4rem;
  padding: 0.3rem 0.5rem;
  width: 100%;
  text-align: center;
}

.covoit-stat--available {
  background: #d1fae5;
  color: #065f46;
}

.covoit-stat--full {
  background: #f1f5f9;
  color: #64748b;
}

.covoit-stat--neutral {
  background: #f1f5f9;
  color: #475569;
}

.covoit-stat__icon {
  font-size: 0.85rem;
  margin-bottom: 0.05rem;
}

.covoit-stat__value {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
}

.covoit-stat__label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
}

.carpool-footer {
  text-align: right;
}

.covoit-card__stats-mobile {
  display: none;
}

.covoit-stat-mini {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.covoit-stat-mini--available {
  background: #d1fae5;
  color: #065f46;
}

.covoit-stat-mini--full {
  background: #f1f5f9;
  color: #64748b;
}

.covoit-stat-mini--neutral {
  background: #f1f5f9;
  color: #475569;
}

@media (max-width: 576px) {
  .covoit-card__avatar {
    width: 68px;
  }

  .covoit-card {
    gap: 0.6rem;
    padding: 0.65rem 0.75rem;
  }

  .covoit-card__stats {
    display: none;
  }

  .covoit-card__stats-mobile {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.4rem;
  }
}
</style>

<style>
/* Tooltip stylé pour les hints du modal d'inscription — non-scoped car BootstrapVue rend les tooltips hors du composant. */
.howto-tooltip.tooltip .tooltip-inner {
  background: #1f2a44;
  color: #f8fbff;
  font-size: 0.82rem;
  line-height: 1.4;
  padding: 0.6rem 0.75rem;
  max-width: 280px;
  text-align: left;
  border-radius: 0.5rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

.howto-tooltip.bs-tooltip-top .arrow::before,
.howto-tooltip.bs-tooltip-auto[x-placement^="top"] .arrow::before {
  border-top-color: #1f2a44;
}

.howto-tooltip.bs-tooltip-bottom .arrow::before,
.howto-tooltip.bs-tooltip-auto[x-placement^="bottom"] .arrow::before {
  border-bottom-color: #1f2a44;
}

.howto-tooltip.bs-tooltip-left .arrow::before,
.howto-tooltip.bs-tooltip-auto[x-placement^="left"] .arrow::before {
  border-left-color: #1f2a44;
}

.howto-tooltip.bs-tooltip-right .arrow::before,
.howto-tooltip.bs-tooltip-auto[x-placement^="right"] .arrow::before {
  border-right-color: #1f2a44;
}
</style>
